import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Laptop,
  Buildings,
  UsersThree,
  CreditCard,
  UploadSimple,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Ticket,
  Info,
  MapPin,
  Image,
  WarningCircle,
} from '@phosphor-icons/react'

const CATEGORIES = [
  { key: 'IT Support', desc: 'System, login, device or software issue', icon: Laptop },
  { key: 'Facilities', desc: 'Building, classroom, room or maintenance issue', icon: Buildings },
  { key: 'HR', desc: 'Staff, access, request or admin support', icon: UsersThree },
  { key: 'Finance', desc: 'Payment, billing, claim or invoice issue', icon: CreditCard },
]

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical']

export default function SubmitTicketPage() {
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [category, setCategory] = useState('')
  const [subject, setSubject] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [description, setDescription] = useState('')
  const [fileName, setFileName] = useState('')

  const selectedCategory = CATEGORIES.find((item) => item.key === category)
  const canContinueStep1 = Boolean(category)
  const canContinueStep2 = Boolean(subject.trim() && description.trim())

  const goToStep = (targetStep) => {
    if (targetStep === 1) setStep(1)
    if (targetStep === 2 && canContinueStep1) setStep(2)
    if (targetStep === 3 && canContinueStep2) setStep(3)
    if (targetStep === 4 && canContinueStep2) setStep(4)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    navigate('/tickets')
  }

  return (
    <div className="page submit-ticket-page">
      <div className="submit-ticket-hero">
        <div>
          <span className="submit-ticket-hero__eyebrow">
            <Ticket size={16} /> Support request
          </span>
          <h1 className="submit-ticket-hero__title">Submit Ticket</h1>
          <p className="submit-ticket-hero__subtitle">
            Create a support request with guided steps and clear information.
          </p>
        </div>

        <div className="submit-ticket-hero__status">
          <span>Step {step} of 4</span>
          <strong>{['Category', 'Details', 'Attachment', 'Review'][step - 1]}</strong>
        </div>
      </div>

      <div className="submit-ticket-layout">
        <form className="submit-ticket-main" onSubmit={handleSubmit}>
          <div className="submit-ticket-steps">
            {['Category', 'Details', 'Attachment', 'Review'].map((label, index) => {
              const number = index + 1

              return (
                <button
                  key={label}
                  type="button"
                  className={`submit-ticket-step ${step === number ? 'is-active' : ''} ${
                    step > number ? 'is-done' : ''
                  }`}
                  onClick={() => goToStep(number)}
                >
                  <span>{number}</span>
                  <strong>{label}</strong>
                </button>
              )
            })}
          </div>

          {step === 1 && (
            <section className="submit-ticket-section">
              <div className="submit-ticket-section__header">
                <h2>Select ticket category</h2>
                <p>Choose the closest category so we can assign it to the right team.</p>
              </div>

              <div className="category-select-grid">
                {CATEGORIES.map((item) => {
                  const Icon = item.icon

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`category-select-card ${
                        category === item.key ? 'is-selected' : ''
                      }`}
                      onClick={() => setCategory(item.key)}
                    >
                      <span className="category-select-card__icon">
                        <Icon size={24} />
                      </span>

                      <span>
                        <strong>{item.key}</strong>
                        <small>{item.desc}</small>
                      </span>
                    </button>
                  )
                })}
              </div>

              <div className="submit-ticket-actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!canContinueStep1}
                  onClick={() => setStep(2)}
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="submit-ticket-section">
              <div className="submit-ticket-section__header">
                <h2>Describe your issue</h2>
                <p>Provide enough details so the support team can understand the issue.</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    className="form-input"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Example: Air conditioner not working"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <div className="priority-pill-group">
                    {PRIORITIES.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`priority-pill ${priority === item ? 'is-selected' : ''}`}
                        onClick={() => setPriority(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group form-group--full">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    rows={7}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please describe what happened, when it happened, and any important details."
                  />
                </div>
              </div>

              <div className="submit-ticket-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setStep(1)}>
                  <ArrowLeft size={16} /> Back
                </button>

                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!canContinueStep2}
                  onClick={() => setStep(3)}
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="submit-ticket-section">
              <div className="submit-ticket-section__header">
                <h2>Add attachment</h2>
                <p>Upload a screenshot, photo, document, or supporting file if needed.</p>
              </div>

              <label className="attachment-dropzone">
                <UploadSimple size={30} />
                <strong>{fileName || 'Click to upload file'}</strong>
                <span>PNG, JPG, PDF, DOCX up to 10MB</span>
                <input
                  type="file"
                  hidden
                  onChange={(e) => setFileName(e.target.files?.[0]?.name || '')}
                />
              </label>

              <div className="submit-ticket-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setStep(2)}>
                  <ArrowLeft size={16} /> Back
                </button>

                <button type="button" className="btn btn--primary" onClick={() => setStep(4)}>
                  Review <ArrowRight size={16} />
                </button>
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="submit-ticket-section">
              <div className="submit-ticket-section__header">
                <h2>Review ticket</h2>
                <p>Please check the details before submitting your ticket.</p>
              </div>

              <div className="review-card">
                <div className="review-card__row">
                  <span>Category</span>
                  <strong>{category || '-'}</strong>
                </div>

                <div className="review-card__row">
                  <span>Subject</span>
                  <strong>{subject || '-'}</strong>
                </div>

                <div className="review-card__row">
                  <span>Priority</span>
                  <strong>{priority}</strong>
                </div>

                <div className="review-card__row">
                  <span>Attachment</span>
                  <strong>{fileName || 'No attachment'}</strong>
                </div>

                <div className="review-card__description">
                  <span>Description</span>
                  <p>{description || '-'}</p>
                </div>
              </div>

              <div className="submit-ticket-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setStep(3)}>
                  <ArrowLeft size={16} /> Back
                </button>

                <button type="submit" className="btn btn--primary">
                  <CheckCircle size={16} /> Submit Ticket
                </button>
              </div>
            </section>
          )}
        </form>

        <aside className="submit-ticket-aside">
          <div className="aside-card aside-card--guide">
            <div className="aside-card__icon">
              <Info size={22} />
            </div>
            <h3>Ticket Guide</h3>
            <p>Select the correct category and provide clear details to help the team respond faster.</p>
          </div>

          <div className="aside-card">
            <h3>Current Selection</h3>

            <div className="aside-summary">
              <span>Category</span>
              <strong>{selectedCategory?.key || 'Not selected'}</strong>
            </div>

            <div className="aside-summary">
              <span>Priority</span>
              <strong>{priority}</strong>
            </div>

            <div className="aside-summary">
              <span>Attachment</span>
              <strong>{fileName ? 'Added' : 'Optional'}</strong>
            </div>
          </div>

          <div className="aside-card">
            <h3>Tips</h3>
            <div className="tip-list">
              <div><MapPin size={18} /> Include location if relevant.</div>
              <div><Image size={18} /> Add photos for physical issues.</div>
              <div><WarningCircle size={18} /> Use High or Critical only for urgent cases.</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
